/**
 * DataTableEditor
 *
 * Property editor for data-table control type
 * Configures table columns, sorting, filtering, pagination, and styling
 *
 * SRP: Responsible ONLY for configuring data-table properties
 * Control Type: 'data-table'
 *
 * Properties Configured (10+):
 * 1. headerBackgroundColor - Table header background
 * 2. headerTextColor - Header text color
 * 3. rowBackgroundColor - Row background color
 * 4. rowHoverColor - Row hover background
 * 5. borderColor - Table border color
 * 6. sortable - Enable column sorting
 * 7. filterable - Enable column filtering
 * 8. pageable - Enable pagination
 * 9. pageSize - Rows per page
 * 10. striped - Alternate row colors
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';

/**
 * Data table configuration
 */
interface DataTableConfig {
  headerBackgroundColor?: string;
  headerTextColor?: string;
  rowBackgroundColor?: string;
  rowHoverColor?: string;
  borderColor?: string;
  sortable?: boolean;
  filterable?: boolean;
  pageable?: boolean;
  pageSize?: number;
  striped?: boolean;
}

/**
 * DataTableEditor Component
 * Renders property editors for all data-table configuration
 */
const DataTableEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as DataTableConfig) || {},
    [control.config],
  );

  const handleHeaderBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('headerBackgroundColor', value),
    [onConfigChange],
  );

  const handleHeaderTextColorChange = useCallback(
    (value: string) => onConfigChange('headerTextColor', value),
    [onConfigChange],
  );

  const handleRowBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('rowBackgroundColor', value),
    [onConfigChange],
  );

  const handleRowHoverColorChange = useCallback(
    (value: string) => onConfigChange('rowHoverColor', value),
    [onConfigChange],
  );

  const handleBorderColorChange = useCallback(
    (value: string) => onConfigChange('borderColor', value),
    [onConfigChange],
  );

  const handleSortableChange = useCallback(
    (value: boolean) => onConfigChange('sortable', value),
    [onConfigChange],
  );

  const handleFilterableChange = useCallback(
    (value: boolean) => onConfigChange('filterable', value),
    [onConfigChange],
  );

  const handlePageableChange = useCallback(
    (value: boolean) => onConfigChange('pageable', value),
    [onConfigChange],
  );

  const handlePageSizeChange = useCallback(
    (value: number) => onConfigChange('pageSize', value),
    [onConfigChange],
  );

  const handleStripedChange = useCallback(
    (value: boolean) => onConfigChange('striped', value),
    [onConfigChange],
  );

  return (
    <div className="af-property-editor__section data-table-editor">
      <h3 className="af-property-editor__section-title">Data Table Properties</h3>

      <ColorPropertyEditor
        label="Header Background Color"
        value={config.headerBackgroundColor || '#F5F5F5'}
        onChange={handleHeaderBackgroundColorChange}
        helpText="Table header background color"
      />

      <ColorPropertyEditor
        label="Header Text Color"
        value={config.headerTextColor || '#000000'}
        onChange={handleHeaderTextColorChange}
        helpText="Table header text color"
      />

      <ColorPropertyEditor
        label="Row Background Color"
        value={config.rowBackgroundColor || '#FFFFFF'}
        onChange={handleRowBackgroundColorChange}
        helpText="Table row background color"
      />

      <ColorPropertyEditor
        label="Row Hover Color"
        value={config.rowHoverColor || '#F0F0F0'}
        onChange={handleRowHoverColorChange}
        helpText="Background color when hovering over row"
      />

      <ColorPropertyEditor
        label="Border Color"
        value={config.borderColor || '#E0E0E0'}
        onChange={handleBorderColorChange}
        helpText="Table border and cell divider color"
      />

      <CheckboxPropertyEditor
        label="Sortable Columns"
        value={config.sortable ?? true}
        onChange={handleSortableChange}
        helpText="Allow users to sort by clicking column headers"
      />

      <CheckboxPropertyEditor
        label="Filterable Columns"
        value={config.filterable || false}
        onChange={handleFilterableChange}
        helpText="Show filter input for each column"
      />

      <CheckboxPropertyEditor
        label="Pageable"
        value={config.pageable ?? true}
        onChange={handlePageableChange}
        helpText="Enable pagination controls"
      />

      <NumberPropertyEditor
        label="Page Size"
        value={config.pageSize || 10}
        onChange={handlePageSizeChange}
        min={5}
        max={100}
        step={5}
        helpText="Number of rows per page"
      />

      <CheckboxPropertyEditor
        label="Striped Rows"
        value={config.striped || false}
        onChange={handleStripedChange}
        helpText="Alternate row background colors"
      />
    </div>
  );
};

DataTableEditorComponent.displayName = 'DataTableEditor';

/**
 * Data Table Property Editor Registration
 */
export const DataTableEditor: PropertyEditor = {
  type: 'data-table',
  label: 'Data Table',
  description: 'Configure table columns, sorting, filtering, pagination, and styling',
  version: '1.0.0',
  render: (props) => <DataTableEditorComponent {...props} />,
};

export default DataTableEditor;
